import { createEffect, sample } from 'effector';
import $$api from 'models/api';
import $$ep from 'models/ep';
import { $isSignupPage, $queryParams, setByKey } from 'models/location';
import $$preselectedAssessment from 'models/preselected-assessment';

import EstatePlanService from 'services/EstatePlanService';

import { EstatePlanProductChoiceZod } from 'types/zod/product-choice';
import { QuestionnaireInstance, QuestionnaireInstanceZod } from 'types/zod/questionnaire-instance';

$$preselectedAssessment.answerFirstProductQuestionFx.use(EstatePlanService.answerForFirstProductQuestion);

// if query param 'product-choice' is present, parse it and set it to the model
sample({
    source: $queryParams.map((params) => params['product-choice']),
    filter: (productChoice) => productChoice !== undefined,
    fn: EstatePlanProductChoiceZod.parse,
    target: $$preselectedAssessment.$productChoice,
});

// if product choice is set and is assessment page, but it's not in the query params, add it to the query params
sample({
    source: { stored: $$preselectedAssessment.$productChoice, isSignupPage: $isSignupPage },
    clock: $queryParams.map((params) => params['product-choice'] || null),
    filter: ({ stored, isSignupPage }, query) => stored !== query && !!stored && !isSignupPage,
    fn: ({ stored: value }) => ({ key: 'product-choice', value }),
    target: setByKey,
});

// if questionnaire is not defined, make a request to get it
sample({
    source: sample({
        source: {
            productChoice: $$preselectedAssessment.$productChoice,
            qInstance: $$ep.stores.$epQInstance,
            questionnaire: $$ep.stores.$epQuestionnaire,
        },
        filter: ({ questionnaire, qInstance }) => !questionnaire && !!qInstance,
        fn: ({ qInstance }) => QuestionnaireInstanceZod.parse(qInstance),
    }),
    fn: ({ state_code, questionnaire_type_code }: QuestionnaireInstance) => ({ state_code, questionnaire_type_code }),
    target: createEffect(
        ({
            state_code,
            questionnaire_type_code,
        }: Pick<QuestionnaireInstance, 'state_code' | 'questionnaire_type_code'>) => {
            $$api.questionnaires.getQuestionnaireFx({ questionnaireType: questionnaire_type_code, state: state_code });
        },
    ),
});

// if user has estate plan and product choice is set, answerFirstProductQuestionFx
sample({
    source: {
        productChoice: $$preselectedAssessment.$productChoice,
        qInstanceId: $$ep.stores.$epQInstanceId,
        questionnaire: $$ep.stores.$epQuestionnaire,
        answers: $$ep.stores.$epAnswers,
        answerSavingIsInProgress: $$preselectedAssessment.answerFirstProductQuestionFx.pending,
    },
    filter: ({ productChoice, qInstanceId, questionnaire, answers, answerSavingIsInProgress }) =>
        !!productChoice && !!qInstanceId && !!questionnaire && !!answers && !answerSavingIsInProgress,
    fn: ({ productChoice }) => EstatePlanProductChoiceZod.parse(productChoice),
    target: $$preselectedAssessment.answerFirstProductQuestionFx,
});

// after answerFirstProductQuestionFx, clear product choice
sample({
    source: $$preselectedAssessment.answerFirstProductQuestionFx.done,
    fn: () => null,
    target: $$preselectedAssessment.$productChoice,
});

// after answerFirstProductQuestionFx, clear query param 'product-choice'
sample({
    source: $$preselectedAssessment.answerFirstProductQuestionFx.done,
    fn: () => ({ key: 'product-choice', value: null }),
    target: setByKey,
});
